import * as React from 'react';
import propz from 'propz';
import { History, Location } from 'history';
import { Form, Formik } from 'formik';
import { NOTIFICATION_STATUS_TYPE, NOTIFICATION_TYPE } from '../../../../../consts/notifications';
import { AppUser } from '../../../../../views/App/App';
import { Loader } from '../../../../../components/Loader/Loader';
import { Switch } from '../../../../../components/Switch/Switch';
import { HelpModal } from '../../../../../components/HelpModal/HelpModal';
import { StatusModal } from '../../../../../components/StatusModal/StatusModal';
import { UnsavedChangesModal } from '../../../../../components/UnsavedChangesModal/UnsavedChangesModal';
import {
  getNotificationSettings,
  updateNotificationSettings
} from '../../../../../helpers/service/adminSU/notificationSettings';
import './NotificationSettings.scss';
import { registerPageHelp } from 'Src/helpers/pageHelp/pageHelp';

interface Props {
  user: AppUser;
  history: History;
  location: Location;
}

type NotificationStatus = 'AUTO' | 'DISABLED';

type NotificationSettingItem = {
  type: string;
  status: NotificationStatus;
};

interface NotificationSchedule {
  tournamentReminderToSchoolOffsetsMinutes: number[];
  tournamentReminderToUnionOffsetsMinutes: number[];
}

type ReminderOffsetScheduleKey = 'tournamentReminderToSchoolOffsetsMinutes' | 'tournamentReminderToUnionOffsetsMinutes';

interface FormValues {
  allSettings: NotificationSettingItem[];
  notificationSchedule: NotificationSchedule;

  tournamentReminderToSchoolCustomDays: string;
  tournamentReminderToSchoolCustomHours: string;
  tournamentReminderToSchoolCustomMinutes: string;

  tournamentReminderToUnionCustomDays: string;
  tournamentReminderToUnionCustomHours: string;
  tournamentReminderToUnionCustomMinutes: string;

  isSchoolNotifications: boolean;
  isUnionNotifications: boolean;
  isHighLevelParticipationNotifications: boolean;
  isAllNotifications: boolean;
}

interface State {
  isLoading: boolean;
  notificationSettings: NotificationSettingItem[];
  notificationSchedule: NotificationSchedule;
  isEditMode: boolean;
  collapsedSections: Record<string, boolean>;
  isLeaveWarningOpen: boolean;
  pendingLeaveLocation: any | null;
  isBypassingNavigationBlock: boolean;

  isStatusModalOpen: boolean;
  statusModalTitle: string;
  statusModalMessage: string;
  statusModalLines: string[];
  statusModalStatus: 'success' | 'error';

  isHelpOpen: boolean;
  highlightedReminderType: string;
}

const CURRENT_NOTIFICATION_TYPE_ARRAY = [
  NOTIFICATION_TYPE.HIGH_LEVEL_TOURNAMENT_AVAILABILITY_REQUEST,
  NOTIFICATION_TYPE.HIGH_LEVEL_TOURNAMENT_AVAILABILITY_DEADLINE,
  NOTIFICATION_TYPE.HIGH_LEVEL_TOURNAMENT_AVAILABILITY_REMINDER,
  NOTIFICATION_TYPE.HIGH_LEVEL_TOURNAMENT_AVAILABILITY_RESPONSE,
  NOTIFICATION_TYPE.HIGH_LEVEL_TOURNAMENT_AVAILABILITY_OVERRIDE,
  NOTIFICATION_TYPE.HIGH_LEVEL_TOURNAMENT_AVAILABILITY_CANCELLED,
  NOTIFICATION_TYPE.SCHOOL_TOURNAMENT_INVITE_CREATED,
  NOTIFICATION_TYPE.SCHOOL_ADDED_TO_TOURNAMENT_PARTICIPANT,
  NOTIFICATION_TYPE.SCHOOL_ADDED_TO_TOURNAMENT_WAITING_LIST,
  NOTIFICATION_TYPE.SCHOOL_REMOVED_FROM_TOURNAMENT,
  NOTIFICATION_TYPE.SCHOOL_REMOVED_FROM_TOURNAMENT_WAITING_LIST,
  NOTIFICATION_TYPE.TOURNAMENT_CUSTOM,
  NOTIFICATION_TYPE.TOURNAMENT_COMMUNICATION,
  NOTIFICATION_TYPE.TOURNAMENT_DETAILS_UPDATED,
  NOTIFICATION_TYPE.CONFIRMATION_REQUEST_CREATED,
  NOTIFICATION_TYPE.TOURNAMENT_CONFIRMATION_REQUEST_ACCEPTED,
  NOTIFICATION_TYPE.TOURNAMENT_CONFIRMATION_REQUEST_REJECTED,
  NOTIFICATION_TYPE.TOURNAMENT_CONFIRMATION_REQUEST_ACCEPTED_SCHOOL,
  NOTIFICATION_TYPE.TOURNAMENT_CONFIRMATION_REQUEST_REJECTED_SCHOOL,
  NOTIFICATION_TYPE.TOURNAMENT_PARTICIPANT_ADDED,
  NOTIFICATION_TYPE.PARTICIPANT_ADDED_TO_TOURNAMENT_WAITING_LIST,
  NOTIFICATION_TYPE.TOURNAMENT_PARTICIPANT_REMOVED,
  NOTIFICATION_TYPE.TOURNAMENT_PARTICIPANT_REMOVED_FROM_WAITING_LIST,
  NOTIFICATION_TYPE.NEW_RECORD_ADDED,
  NOTIFICATION_TYPE.TOURNAMENT_REMINDER_TO_UNION,
  NOTIFICATION_TYPE.TOURNAMENT_REMINDER_TO_SCHOOL
];

const SETTING_DESTINATIONS_TYPE = {
  SCHOOL_NOTIFICATIONS: 'SCHOOL_NOTIFICATIONS',
  UNION_NOTIFICATIONS: 'UNION_NOTIFICATIONS',
  HIGH_LEVEL_PARTICIPATION: 'HIGH_LEVEL_PARTICIPATION'
} as const;

const SETTINGS_DESIGNATIONS = [
  { type: SETTING_DESTINATIONS_TYPE.SCHOOL_NOTIFICATIONS, name: 'Notifications sent to school admins' },
  { type: SETTING_DESTINATIONS_TYPE.UNION_NOTIFICATIONS, name: 'Notifications sent to union admins' },
  { type: SETTING_DESTINATIONS_TYPE.HIGH_LEVEL_PARTICIPATION, name: 'High-level tournaments · Union participation',
    description: 'For tournaments where your union participates: availability requests to athletes’ schools and school responses to your union. Choose the receiving union administrators using Notification recipients inside each tournament.' }
];

const SETTINGS = [
  {type: NOTIFICATION_TYPE.HIGH_LEVEL_TOURNAMENT_AVAILABILITY_REQUEST, relation: SETTING_DESTINATIONS_TYPE.HIGH_LEVEL_PARTICIPATION,
    name: 'Student availability request', hintText: 'Sent to: School admins and managers\nSent when your union asks schools to confirm student availability. Schools respond in the web console.'},
  {type: NOTIFICATION_TYPE.HIGH_LEVEL_TOURNAMENT_AVAILABILITY_DEADLINE, relation: SETTING_DESTINATIONS_TYPE.HIGH_LEVEL_PARTICIPATION,
    name: 'Student availability deadline changed', hintText: 'Sent to: School admins and managers\nSent when the availability deadline is updated. Existing responses are retained.'},
  {type: NOTIFICATION_TYPE.HIGH_LEVEL_TOURNAMENT_AVAILABILITY_REMINDER, relation: SETTING_DESTINATIONS_TYPE.HIGH_LEVEL_PARTICIPATION,
    name: 'Student availability reminder', hintText: 'Sent to: School admins and managers\nReminds schools with pending student responses. Automatic reminder timing is set in each availability request.'},
  {type: NOTIFICATION_TYPE.HIGH_LEVEL_TOURNAMENT_AVAILABILITY_OVERRIDE, relation: SETTING_DESTINATIONS_TYPE.HIGH_LEVEL_PARTICIPATION,
    name: 'Student availability changed by union', hintText: 'Sent to: School admins and managers\nSent when your union overrides a student availability status.'},
  {type: NOTIFICATION_TYPE.HIGH_LEVEL_TOURNAMENT_AVAILABILITY_CANCELLED, relation: SETTING_DESTINATIONS_TYPE.HIGH_LEVEL_PARTICIPATION,
    name: 'Student availability request cancelled', hintText: 'Sent to: School admins and managers\nSent when your union cancels an availability request.'},
  {type: NOTIFICATION_TYPE.HIGH_LEVEL_TOURNAMENT_AVAILABILITY_RESPONSE, relation: SETTING_DESTINATIONS_TYPE.HIGH_LEVEL_PARTICIPATION,
    name: 'School saved student availability', hintText: 'Sent to: Participating union admins\nSent after a school saves a full or partial response, including withdrawals. Includes the available, not available and pending totals. Recipients follow the Notification recipients setting inside that tournament.'},
  {
    type: NOTIFICATION_TYPE.SCHOOL_TOURNAMENT_INVITE_CREATED,
    relation: SETTING_DESTINATIONS_TYPE.SCHOOL_NOTIFICATIONS,
    name: 'Tournament invite has been created',
    hintText: 'Sent to: School admins\n' + 'Sent when a tournament invitation is issued by a union admin.'
  },
  {
    type: NOTIFICATION_TYPE.SCHOOL_ADDED_TO_TOURNAMENT_PARTICIPANT,
    relation: SETTING_DESTINATIONS_TYPE.SCHOOL_NOTIFICATIONS,
    name: 'School has been added as a tournament participant',
    hintText:
      'Sent to: School admins\n' +
      'Sent once a school is added to the tournament participant list, whether through booking or direct addition by a union admin.'
  },
  {
    type: NOTIFICATION_TYPE.SCHOOL_ADDED_TO_TOURNAMENT_WAITING_LIST,
    relation: SETTING_DESTINATIONS_TYPE.SCHOOL_NOTIFICATIONS,
    name: 'School has been added to a tournament waiting list',
    hintText: 'Sent to: School admins\nSent when a school is added to the tournament waiting list.'
  },
  {
    type: NOTIFICATION_TYPE.SCHOOL_REMOVED_FROM_TOURNAMENT_WAITING_LIST,
    relation: SETTING_DESTINATIONS_TYPE.SCHOOL_NOTIFICATIONS,
    name: 'School has been removed from a tournament waiting list',
    hintText:
      'Sent to: School admins\n' + 'Sent when your school leaves or is removed from the tournament waiting list.'
  },
  {
    type: NOTIFICATION_TYPE.SCHOOL_REMOVED_FROM_TOURNAMENT,
    relation: SETTING_DESTINATIONS_TYPE.SCHOOL_NOTIFICATIONS,
    name: 'School has been removed from a tournament',
    hintText:
      'Sent to: School admins\n' +
      'Sent after a school is removed from the tournament, either through the Leave the tournament action or by direct removal by a union admin.'
  },
  {
    type: NOTIFICATION_TYPE.TOURNAMENT_CUSTOM,
    relation: SETTING_DESTINATIONS_TYPE.SCHOOL_NOTIFICATIONS,
    name: 'Custom notification',
    hintText: 'Sent to: School admins\nSent to selected schools by a union admin.'
  },
  {
    type: NOTIFICATION_TYPE.TOURNAMENT_COMMUNICATION,
    relation: SETTING_DESTINATIONS_TYPE.SCHOOL_NOTIFICATIONS,
    name: 'Tournament chat messages and announcements',
    hintText:
      'Sent to: Relevant school or tournament staff\nSent when a new private tournament message or targeted tournament announcement is posted.'
  },
  {
    type: NOTIFICATION_TYPE.TOURNAMENT_DETAILS_UPDATED,
    relation: SETTING_DESTINATIONS_TYPE.SCHOOL_NOTIFICATIONS,
    name: 'Tournament details have been updated',
    hintText:
      'Sent to: School admins\n' +
      'Sent when a union admin updates the tournament details, including changes to the date, timing, or venue.'
  },
  {
    type: NOTIFICATION_TYPE.CONFIRMATION_REQUEST_CREATED,
    relation: SETTING_DESTINATIONS_TYPE.SCHOOL_NOTIFICATIONS,
    name: 'Confirmation request has been received',
    hintText:
      'Sent to: School admins\n' + 'Sent when a union admin sends a tournament participation confirmation request.'
  },
  {
    type: NOTIFICATION_TYPE.TOURNAMENT_CONFIRMATION_REQUEST_ACCEPTED_SCHOOL,
    relation: SETTING_DESTINATIONS_TYPE.SCHOOL_NOTIFICATIONS,
    name: 'Participation confirmation has been submitted',
    hintText:
      'Sent to: School admins\n' +
      'Sent when your school confirms participation in response to a tournament confirmation request.'
  },
  {
    type: NOTIFICATION_TYPE.TOURNAMENT_CONFIRMATION_REQUEST_REJECTED_SCHOOL,
    relation: SETTING_DESTINATIONS_TYPE.SCHOOL_NOTIFICATIONS,
    name: 'Non-participation confirmation has been submitted',
    hintText:
      'Sent to: School admins\n' +
      'Sent when your school confirms that it is not participating in response to a tournament confirmation request.'
  },
  {
    type: NOTIFICATION_TYPE.TOURNAMENT_REMINDER_TO_SCHOOL,
    relation: SETTING_DESTINATIONS_TYPE.SCHOOL_NOTIFICATIONS,
    name: 'Tournament reminder',
    hintText:
      'Sent to: School admins\n' +
      'Reminder sent to school admins before a tournament starts, based on the configured reminder schedule.'
  },
  {
    type: NOTIFICATION_TYPE.TOURNAMENT_CONFIRMATION_REQUEST_ACCEPTED,
    relation: SETTING_DESTINATIONS_TYPE.UNION_NOTIFICATIONS,
    name: 'School has confirmed participation',
    hintText:
      'Sent to: Union admins\n' +
      'Sent when a school responds to a tournament confirmation request and confirms participation.'
  },
  {
    type: NOTIFICATION_TYPE.TOURNAMENT_CONFIRMATION_REQUEST_REJECTED,
    relation: SETTING_DESTINATIONS_TYPE.UNION_NOTIFICATIONS,
    name: 'School has declined participation',
    hintText:
      'Sent to: Union admins\n' +
      'Sent when a school responds to a tournament confirmation request and confirms that it is not participating.'
  },
  {
    type: NOTIFICATION_TYPE.TOURNAMENT_PARTICIPANT_ADDED,
    relation: SETTING_DESTINATIONS_TYPE.UNION_NOTIFICATIONS,
    name: 'School has been added to the tournament participant list',
    hintText:
      'Sent to: Union admins\n' +
      'Sent once a school is added to the tournament participant list, whether through booking or direct addition by a union admin.'
  },
  {
    type: NOTIFICATION_TYPE.PARTICIPANT_ADDED_TO_TOURNAMENT_WAITING_LIST,
    relation: SETTING_DESTINATIONS_TYPE.UNION_NOTIFICATIONS,
    name: 'School has been added to a tournament waiting list',
    hintText: 'Sent to: Union admins\nSent when a school is added to the tournament waiting list.'
  },
  {
    type: NOTIFICATION_TYPE.TOURNAMENT_PARTICIPANT_REMOVED_FROM_WAITING_LIST,
    relation: SETTING_DESTINATIONS_TYPE.UNION_NOTIFICATIONS,
    name: 'School has been removed from a tournament waiting list',
    hintText: 'Sent to: Union admins\n' + 'Sent when a school leaves or is removed from the tournament waiting list.'
  },
  {
    type: NOTIFICATION_TYPE.TOURNAMENT_PARTICIPANT_REMOVED,
    relation: SETTING_DESTINATIONS_TYPE.UNION_NOTIFICATIONS,
    name: 'School has been removed from a tournament',
    hintText:
      'Sent to: Union admins\n' +
      'Sent after a school is removed from the tournament, either through the Leave the tournament action or by direct removal by a union admin.'
  },
  {
    type: NOTIFICATION_TYPE.NEW_RECORD_ADDED,
    relation: SETTING_DESTINATIONS_TYPE.UNION_NOTIFICATIONS,
    name: 'New record has been added',
    hintText: 'Sent to: Union admins\nSent after a new record is added either automatically or manually.'
  },
  {
    type: NOTIFICATION_TYPE.TOURNAMENT_REMINDER_TO_UNION,
    relation: SETTING_DESTINATIONS_TYPE.UNION_NOTIFICATIONS,
    name: 'Tournament reminder',
    hintText:
      'Sent to: Union admins\n' +
      'Reminder sent to union admins before a tournament starts, based on the configured reminder schedule.'
  }
];

const REMINDER_PRESET_OPTIONS = [
  { value: 10080, label: '7 days' },
  { value: 4320, label: '3 days' },
  { value: 2880, label: '2 days' },
  { value: 1440, label: '1 day' },
  { value: 720, label: '12 hours' },
  { value: 360, label: '6 hours' },
  { value: 180, label: '3 hours' },
  { value: 60, label: '1 hour' },
  { value: 30, label: '30 minutes' }
];

const normalizeReminderOffsets = (input: number[]): number[] => {
  if (!Array.isArray(input)) return [];

  return Array.from(
    new Set(
      input
        .map(v => Number(v))
        .filter(v => Number.isFinite(v) && v > 0)
        .map(v => Math.round(v))
    )
  ).sort((a, b) => b - a);
};

const formatReminderOffsetLabel = (minutes: number): string => {
  const safeMinutes = Math.round(Number(minutes) || 0);
  if (safeMinutes <= 0) return '';

  const days = Math.floor(safeMinutes / 1440);
  const remainingAfterDays = safeMinutes % 1440;
  const hours = Math.floor(remainingAfterDays / 60);
  const mins = remainingAfterDays % 60;

  const parts: string[] = [];

  if (days > 0) parts.push(`${days} day${days === 1 ? '' : 's'}`);
  if (hours > 0) parts.push(`${hours} hour${hours === 1 ? '' : 's'}`);
  if (mins > 0) parts.push(`${mins} minute${mins === 1 ? '' : 's'}`);

  return parts.join(' ');
};

const isPresetReminderOffset = (minutes: number): boolean => {
  return REMINDER_PRESET_OPTIONS.some(option => option.value === minutes);
};

const getReminderOffsetArray = (schedule: NotificationSchedule, key: ReminderOffsetScheduleKey): number[] => {
  const value = schedule[key];
  return Array.isArray(value) ? value : [];
};

export class UnionNotificationSettings extends React.Component<Props, State> {
  private formValuesRef: FormValues | null = null;
  private unblockNavigation: (() => void) | null = null;
  private unregisterPageHelp: (() => void) | null = null;

  constructor(props: Props) {
    super(props);

    this.state = {
      isLoading: false,
      notificationSettings: [],
      notificationSchedule: {
        tournamentReminderToSchoolOffsetsMinutes: [],
        tournamentReminderToUnionOffsetsMinutes: []
      },
      isEditMode: false,
      collapsedSections: {
        [SETTING_DESTINATIONS_TYPE.SCHOOL_NOTIFICATIONS]: true,
        [SETTING_DESTINATIONS_TYPE.UNION_NOTIFICATIONS]: true,
        [SETTING_DESTINATIONS_TYPE.HIGH_LEVEL_PARTICIPATION]: true
      },
      isLeaveWarningOpen: false,
      pendingLeaveLocation: null,
      isBypassingNavigationBlock: false,

      isStatusModalOpen: false,
      statusModalTitle: '',
      statusModalMessage: '',
      statusModalLines: [],
      statusModalStatus: 'success',

      isHelpOpen: false,
      highlightedReminderType: ''
    };
  }

  componentDidMount() {
    this.setState({ isLoading: true });
    this.getNotificationSettingsFromNotificationSettingsObject();

    this.unblockNavigation = this.props.history.block(nextLocation => {
      if (this.state.isBypassingNavigationBlock || !this.hasUnsavedChanges()) return;

      this.setState({
        isLeaveWarningOpen: true,
        pendingLeaveLocation: nextLocation
      });
      return false;
    });

    window.addEventListener('beforeunload', this.handleBeforeUnload);
    this.unregisterPageHelp = registerPageHelp(this.openHelpModal, 'Help for notification settings');
  }

  componentWillUnmount() {
    if (this.unblockNavigation) this.unblockNavigation();
    window.removeEventListener('beforeunload', this.handleBeforeUnload);
    if (this.unregisterPageHelp) this.unregisterPageHelp();
  }

  openHelpModal = (): void => {
    this.setState({ isHelpOpen: true });
  };

  handleBeforeUnload = (event: BeforeUnloadEvent): string | void => {
    if (!this.hasUnsavedChanges()) return;
    event.preventDefault();
    event.returnValue = '';
    return '';
  };

  getNotificationSettingsFromNotificationSettingsObject = (): void => {
    const user = this.props.user;

    getNotificationSettings(user).then(notificationSettingsResponse => {
      const notificationsObject = propz.get(notificationSettingsResponse, ['notifications'], {});
      const notificationSchedule = propz.get(
        notificationSettingsResponse,
        ['notificationSchedule'],
        {} as NotificationSchedule
      ) as NotificationSchedule;

      const notificationSettings = this.getArrayFromObjectDesiredValues(notificationsObject);

      this.setState({
        notificationSettings,
        notificationSchedule: {
          tournamentReminderToSchoolOffsetsMinutes: normalizeReminderOffsets(
            Array.isArray(notificationSchedule.tournamentReminderToSchoolOffsetsMinutes)
              ? notificationSchedule.tournamentReminderToSchoolOffsetsMinutes
              : []
          ),
          tournamentReminderToUnionOffsetsMinutes: normalizeReminderOffsets(
            Array.isArray(notificationSchedule.tournamentReminderToUnionOffsetsMinutes)
              ? notificationSchedule.tournamentReminderToUnionOffsetsMinutes
              : []
          )
        },
        isLoading: false
      });
    });
  };

  getArrayFromObjectDesiredValues = (object: Record<string, NotificationStatus>): NotificationSettingItem[] => {
    // Missing legacy/new fields are off, rather than absent from group-state calculations.
    return CURRENT_NOTIFICATION_TYPE_ARRAY.map(type => ({
      type,
      status: object && object[type] === NOTIFICATION_STATUS_TYPE.AUTO
        ? NOTIFICATION_STATUS_TYPE.AUTO as NotificationStatus : NOTIFICATION_STATUS_TYPE.DISABLED as NotificationStatus
    })).sort((a, b) => String(a.type).localeCompare(String(b.type)));
  };

  getNotificationSettingsObjectFromNotificationSettings = (
    arrayWithData: NotificationSettingItem[]
  ): Record<string, NotificationStatus> => {
    const objectData: Record<string, NotificationStatus> = {};
    arrayWithData.forEach(item => {
      objectData[item.type] = item.status;
    });
    return objectData;
  };

  getNotificationStatus = (status: boolean): NotificationStatus => {
    return status
      ? (NOTIFICATION_STATUS_TYPE.AUTO as NotificationStatus)
      : (NOTIFICATION_STATUS_TYPE.DISABLED as NotificationStatus);
  };

  getReverseStatus = (status: string): NotificationStatus => {
    return status === NOTIFICATION_STATUS_TYPE.AUTO
      ? (NOTIFICATION_STATUS_TYPE.DISABLED as NotificationStatus)
      : (NOTIFICATION_STATUS_TYPE.AUTO as NotificationStatus);
  };

  getIsStatusAuto = (status: string): boolean => {
    return status === NOTIFICATION_STATUS_TYPE.AUTO;
  };

  getAllNotificationsStatus = (notificationSettings: NotificationSettingItem[]): boolean => {
    return [...notificationSettings].every(setting => setting.status === NOTIFICATION_STATUS_TYPE.AUTO);
  };

  getGroupSettingStatus = (notificationSettings: NotificationSettingItem[], type: string): boolean => {
    const settings = SETTINGS.filter(setting => setting.relation === type);
    const someSettings = notificationSettings.filter(adjustment =>
      settings.some(setting => setting.type === adjustment.type)
    );

    if (someSettings.length === 0) return false;

    return someSettings.every(setting => setting.status === NOTIFICATION_STATUS_TYPE.AUTO);
  };

  getGroupVisualState = (
    notificationSettings: NotificationSettingItem[],
    type: string
  ): 'all-on' | 'all-off' | 'mixed' => {
    const settings = SETTINGS.filter(setting => setting.relation === type);
    const groupItems = notificationSettings.filter(item => settings.some(setting => setting.type === item.type));

    if (groupItems.length === 0) return 'all-off';

    const autoCount = groupItems.filter(item => item.status === NOTIFICATION_STATUS_TYPE.AUTO).length;

    if (autoCount === 0) return 'all-off';
    if (autoCount === groupItems.length) return 'all-on';
    return 'mixed';
  };

  getCheckType = (type: string): 'isSchoolNotifications' | 'isUnionNotifications' | 'isHighLevelParticipationNotifications' => {
    switch (type) {
      case SETTING_DESTINATIONS_TYPE.SCHOOL_NOTIFICATIONS:
        return 'isSchoolNotifications';
      case SETTING_DESTINATIONS_TYPE.HIGH_LEVEL_PARTICIPATION:
        return 'isHighLevelParticipationNotifications';
      case SETTING_DESTINATIONS_TYPE.UNION_NOTIFICATIONS:
      default:
        return 'isUnionNotifications';
    }
  };

  getShortHintText = (hintText: string): string => {
    const text = String(hintText || '').trim();
    if (!text) return '';

    const lines = text
      .split('\n')
      .map(line => line.trim())
      .filter(Boolean);

    const withoutSentTo = lines.filter(line => !/^Sent to:/i.test(line));
    return withoutSentTo[0] || lines[0] || '';
  };

  getRecipientLabel = (hintText: string): string => {
    const text = String(hintText || '').trim();
    if (!text) return '';

    const lines = text
      .split('\n')
      .map(line => line.trim())
      .filter(Boolean);

    const sentToLine = lines.find(line => /^Sent to:/i.test(line));
    if (!sentToLine) return '';

    return sentToLine.replace(/^Sent to:\s*/i, '').trim();
  };

  toggleSectionCollapsed = (sectionType: string): void => {
    this.setState(prevState => ({
      collapsedSections: {
        ...prevState.collapsedSections,
        [sectionType]: !prevState.collapsedSections[sectionType]
      }
    }));
  };

  expandAllSections = (): void => {
    this.setState({
      collapsedSections: {
        [SETTING_DESTINATIONS_TYPE.SCHOOL_NOTIFICATIONS]: false,
        [SETTING_DESTINATIONS_TYPE.UNION_NOTIFICATIONS]: false,
        [SETTING_DESTINATIONS_TYPE.HIGH_LEVEL_PARTICIPATION]: false
      }
    });
  };

  collapseAllSections = (): void => {
    this.setState({
      collapsedSections: {
        [SETTING_DESTINATIONS_TYPE.SCHOOL_NOTIFICATIONS]: true,
        [SETTING_DESTINATIONS_TYPE.UNION_NOTIFICATIONS]: true,
        [SETTING_DESTINATIONS_TYPE.HIGH_LEVEL_PARTICIPATION]: true
      }
    });
  };

  toggleReminderOffset = (
    values: FormValues,
    setFieldValue: (field: string, value: any) => void,
    scheduleKey: ReminderOffsetScheduleKey,
    minutes: number
  ): void => {
    const current = getReminderOffsetArray(values.notificationSchedule, scheduleKey);

    const next: number[] = current.includes(minutes)
      ? current.filter(v => v !== minutes)
      : normalizeReminderOffsets([...current, minutes]);

    setFieldValue(`notificationSchedule.${scheduleKey}`, next);
  };

  addCustomReminderOffset = (
    values: FormValues,
    setFieldValue: (field: string, value: any) => void,
    scheduleKey: ReminderOffsetScheduleKey,
    inputPrefix: 'tournamentReminderToSchoolCustom' | 'tournamentReminderToUnionCustom'
  ): void => {
    const rawDays = String(values[`${inputPrefix}Days`] || '').trim();
    const rawHours = String(values[`${inputPrefix}Hours`] || '').trim();
    const rawMinutes = String(values[`${inputPrefix}Minutes`] || '').trim();

    const days = rawDays === '' ? 0 : Number(rawDays);
    const hours = rawHours === '' ? 0 : Number(rawHours);
    const minutes = rawMinutes === '' ? 0 : Number(rawMinutes);

    if (
      !Number.isFinite(days) ||
      !Number.isFinite(hours) ||
      !Number.isFinite(minutes) ||
      days < 0 ||
      hours < 0 ||
      minutes < 0
    ) {
      return;
    }

    const totalMinutes = Math.round(days * 1440 + hours * 60 + minutes);
    if (totalMinutes <= 0) return;

    const current = getReminderOffsetArray(values.notificationSchedule, scheduleKey);
    const next: number[] = normalizeReminderOffsets([...current, totalMinutes]);

    setFieldValue(`notificationSchedule.${scheduleKey}`, next);
    setFieldValue(`${inputPrefix}Days`, '');
    setFieldValue(`${inputPrefix}Hours`, '');
    setFieldValue(`${inputPrefix}Minutes`, '');
  };

  removeReminderOffset = (
    values: FormValues,
    setFieldValue: (field: string, value: any) => void,
    scheduleKey: ReminderOffsetScheduleKey,
    minutes: number
  ): void => {
    const current = getReminderOffsetArray(values.notificationSchedule, scheduleKey);
    const next: number[] = current.filter(v => v !== minutes);
    setFieldValue(`notificationSchedule.${scheduleKey}`, next);
  };

  renderReminderScheduleInline = (
    values: FormValues,
    setFieldValue: (field: string, value: any) => void,
    scheduleKey: ReminderOffsetScheduleKey,
    inputPrefix: 'tournamentReminderToSchoolCustom' | 'tournamentReminderToUnionCustom',
    title: string
  ): React.ReactNode => {
    const selectedValues = getReminderOffsetArray(values.notificationSchedule, scheduleKey);
    const customValues = selectedValues.filter(value => !isPresetReminderOffset(value));

    const customDays = values[`${inputPrefix}Days`] || '';
    const customHours = values[`${inputPrefix}Hours`] || '';
    const customMinutes = values[`${inputPrefix}Minutes`] || '';

    const previewDays = customDays === '' ? 0 : Number(customDays);
    const previewHours = customHours === '' ? 0 : Number(customHours);
    const previewMinutes = customMinutes === '' ? 0 : Number(customMinutes);

    const previewTotalMinutes =
      (Number.isFinite(previewDays) ? previewDays : 0) * 1440 +
      (Number.isFinite(previewHours) ? previewHours : 0) * 60 +
      (Number.isFinite(previewMinutes) ? previewMinutes : 0);

    const hasPreviewValue = previewTotalMinutes > 0;

    return (
      <div className="nsScheduleCard">
        <div className="nsScheduleTitle">{title}</div>
        <div className="nsScheduleText">Choose when this reminder should be sent.</div>

        <div className="nsPresetList">
          {REMINDER_PRESET_OPTIONS.map(option => {
            const isSelected = selectedValues.includes(option.value);

            return (
              <button
                key={`${scheduleKey}_${option.value}`}
                type="button"
                className={`nsPresetChip ${isSelected ? 'is-selected' : ''}`}
                aria-pressed={isSelected}
                disabled={!this.state.isEditMode}
                onClick={() => this.toggleReminderOffset(values, setFieldValue, scheduleKey, option.value)}
              >
                {option.label}
              </button>
            );
          })}
        </div>

        <div className="nsScheduleEditor">
          <div className="nsCustomBuilder">
            <div className="nsCustomField">
              <div className="nsCustomLabel">Days</div>
              <input
                type="number"
                min={0}
                step={1}
                value={customDays}
                disabled={!this.state.isEditMode}
                onChange={event => setFieldValue(`${inputPrefix}Days`, event.target.value)}
                className="nsCustomInput"
                placeholder="0"
              />
            </div>

            <div className="nsCustomField">
              <div className="nsCustomLabel">Hours</div>
              <input
                type="number"
                min={0}
                step={1}
                value={customHours}
                disabled={!this.state.isEditMode}
                onChange={event => setFieldValue(`${inputPrefix}Hours`, event.target.value)}
                className="nsCustomInput"
                placeholder="0"
              />
            </div>

            <div className="nsCustomField">
              <div className="nsCustomLabel">Minutes</div>
              <input
                type="number"
                min={0}
                step={1}
                value={customMinutes}
                disabled={!this.state.isEditMode}
                onChange={event => setFieldValue(`${inputPrefix}Minutes`, event.target.value)}
                className="nsCustomInput"
                placeholder="0"
              />
            </div>

            <button
              type="button"
              className="nsCustomAction"
              disabled={!this.state.isEditMode}
              onClick={() => this.addCustomReminderOffset(values, setFieldValue, scheduleKey, inputPrefix)}
            >
              Add custom interval
            </button>
          </div>

          <div className="nsCustomHint">Add a custom reminder interval using days, hours and minutes.</div>

          {hasPreviewValue ? (
            <div className="nsPreviewLine">
              Preview: <span className="nsPreviewStrong">{formatReminderOffsetLabel(previewTotalMinutes)}</span>
            </div>
          ) : null}

          {customValues.length > 0 ? (
            <div className="nsChipRow">
              {customValues.map(value => (
                <div key={`${scheduleKey}_custom_${value}`} className="nsChip nsChip--custom">
                  <span>{formatReminderOffsetLabel(value)}</span>
                  <button
                    type="button"
                    className="nsChipRemove"
                    disabled={!this.state.isEditMode}
                    onClick={() => this.removeReminderOffset(values, setFieldValue, scheduleKey, value)}
                    title="Remove custom interval"
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>
          ) : null}
        </div>

        <div className="nsSelectedText">
          Selected:{' '}
          {selectedValues.length > 0
            ? selectedValues.map(formatReminderOffsetLabel).join(', ')
            : 'No reminder intervals selected'}
        </div>
      </div>
    );
  };

  onCloseClick = (): void => {
    if (this.hasUnsavedChanges()) {
      this.setState({ isLeaveWarningOpen: true, pendingLeaveLocation: null });
      return;
    }

    this.discardChangesAndClose();
  };

  discardChangesAndClose = (): void => {
    this.setState({
      isEditMode: false,
      isLoading: true,
      isStatusModalOpen: false,
      statusModalTitle: '',
      statusModalMessage: '',
      statusModalLines: [],
      statusModalStatus: 'success',
      highlightedReminderType: ''
    });
    this.getNotificationSettingsFromNotificationSettingsObject();
  };

  hasUnsavedChanges = (): boolean => {
    const current = this.formValuesRef;
    if (!current || !this.state.isEditMode) return false;

    const normalizeSettings = (settings: NotificationSettingItem[]) =>
      JSON.stringify(
        [...(settings || [])]
          .map(item => ({ type: item.type, status: item.status }))
          .sort((a, b) => String(a.type).localeCompare(String(b.type)))
      );

    const savedSchedule = this.state.notificationSchedule;
    const currentSchedule = current.notificationSchedule;
    const normalizeSchedule = (schedule: NotificationSchedule) =>
      JSON.stringify({
        tournamentReminderToSchoolOffsetsMinutes: normalizeReminderOffsets(
          schedule.tournamentReminderToSchoolOffsetsMinutes
        ),
        tournamentReminderToUnionOffsetsMinutes: normalizeReminderOffsets(
          schedule.tournamentReminderToUnionOffsetsMinutes
        )
      });

    return (
      normalizeSettings(this.state.notificationSettings) !== normalizeSettings(current.allSettings) ||
      normalizeSchedule(savedSchedule) !== normalizeSchedule(currentSchedule)
    );
  };

  onStayOnPage = (): void => {
    this.setState({ isLeaveWarningOpen: false, pendingLeaveLocation: null });
  };

  onDiscardChanges = (): void => {
    const pendingLeaveLocation = this.state.pendingLeaveLocation;

    this.setState({ isLeaveWarningOpen: false, isBypassingNavigationBlock: true }, () => {
      if (pendingLeaveLocation) {
        this.props.history.push(pendingLeaveLocation);
      } else {
        this.discardChangesAndClose();
      }

      window.setTimeout(() => {
        this.setState({ pendingLeaveLocation: null, isBypassingNavigationBlock: false });
      }, 0);
    });
  };

  onFormSubmit = (values): void => {
    const { user } = this.props;
    const { isEditMode } = this.state;

    if (!isEditMode) {
      this.setState({
        isEditMode: true
      });
      return;
    }

    this.setState({
      isLoading: true
    });

    const notificationSettings = this.getNotificationSettingsObjectFromNotificationSettings(values.allSettings);

    const tournamentReminderToSchoolOffsetsMinutes = Array.isArray(
      propz.get(values, ['notificationSchedule', 'tournamentReminderToSchoolOffsetsMinutes'], [])
    )
      ? normalizeReminderOffsets(
          propz.get(values, ['notificationSchedule', 'tournamentReminderToSchoolOffsetsMinutes'], [])
        )
      : [];

    const tournamentReminderToUnionOffsetsMinutes = Array.isArray(
      propz.get(values, ['notificationSchedule', 'tournamentReminderToUnionOffsetsMinutes'], [])
    )
      ? normalizeReminderOffsets(
          propz.get(values, ['notificationSchedule', 'tournamentReminderToUnionOffsetsMinutes'], [])
        )
      : [];

    const data = {
      ...notificationSettings,
      notificationSchedule: {
        tournamentReminderToSchoolOffsetsMinutes,
        tournamentReminderToUnionOffsetsMinutes
      }
    };

    const tournamentReminderToSchoolStatus = data[NOTIFICATION_TYPE.TOURNAMENT_REMINDER_TO_SCHOOL];
    const tournamentReminderToUnionStatus = data[NOTIFICATION_TYPE.TOURNAMENT_REMINDER_TO_UNION];

    let validationError = '';
    let validationSection = '';
    let highlightedReminderType = '';

    if (
      tournamentReminderToSchoolStatus === NOTIFICATION_STATUS_TYPE.AUTO &&
      tournamentReminderToSchoolOffsetsMinutes.length === 0
    ) {
      validationError = 'Tournament reminder to school cannot be enabled without at least one reminder interval.';
      validationSection = SETTING_DESTINATIONS_TYPE.SCHOOL_NOTIFICATIONS;
      highlightedReminderType = NOTIFICATION_TYPE.TOURNAMENT_REMINDER_TO_SCHOOL;
    } else if (
      tournamentReminderToUnionStatus === NOTIFICATION_STATUS_TYPE.AUTO &&
      tournamentReminderToUnionOffsetsMinutes.length === 0
    ) {
      validationError = 'Tournament reminder to union cannot be enabled without at least one reminder interval.';
      validationSection = SETTING_DESTINATIONS_TYPE.UNION_NOTIFICATIONS;
      highlightedReminderType = NOTIFICATION_TYPE.TOURNAMENT_REMINDER_TO_UNION;
    }

    if (validationError) {
      this.setState(prevState => ({
        isLoading: false,
        isStatusModalOpen: true,
        statusModalStatus: 'error',
        statusModalTitle: 'Unable to save notification settings',
        statusModalMessage: validationError,
        statusModalLines: [],
        highlightedReminderType,
        collapsedSections: validationSection
          ? {
              ...prevState.collapsedSections,
              [validationSection]: false
            }
          : prevState.collapsedSections
      }));
      return;
    }

    updateNotificationSettings(user, data)
      .then(notificationSettingsResponse => {
        const notificationsObject = propz.get(notificationSettingsResponse, ['notifications'], {});
        const notificationSchedule = propz.get(
          notificationSettingsResponse,
          ['notificationSchedule'],
          {} as NotificationSchedule
        ) as NotificationSchedule;

        const arrayNotificationSettings = this.getArrayFromObjectDesiredValues(notificationsObject);

        this.setState({
          notificationSettings: arrayNotificationSettings,
          notificationSchedule: {
            tournamentReminderToSchoolOffsetsMinutes: Array.isArray(
              notificationSchedule.tournamentReminderToSchoolOffsetsMinutes
            )
              ? normalizeReminderOffsets(notificationSchedule.tournamentReminderToSchoolOffsetsMinutes)
              : [],
            tournamentReminderToUnionOffsetsMinutes: Array.isArray(
              notificationSchedule.tournamentReminderToUnionOffsetsMinutes
            )
              ? normalizeReminderOffsets(notificationSchedule.tournamentReminderToUnionOffsetsMinutes)
              : []
          },
          isEditMode: false,
          isLoading: false,
          isStatusModalOpen: true,
          statusModalStatus: 'success',
          statusModalTitle: 'Notification settings saved',
          statusModalMessage: 'Your changes have been saved successfully.',
          statusModalLines: ['The updated notification settings are now active for your union.'],
          highlightedReminderType: ''
        });
      })
      .catch(err => {
        console.error('Union notification settings save error:', err);

        const errorText = propz.get(
          err,
          ['response', 'data', 'details', 'text'],
          propz.get(err, ['response', 'data', 'error'], 'Failed to save notification settings.')
        );

        let backendValidationSection = '';
        let backendHighlightedReminderType = '';

        if (/tournament reminder to school/i.test(errorText)) {
          backendValidationSection = SETTING_DESTINATIONS_TYPE.SCHOOL_NOTIFICATIONS;
          backendHighlightedReminderType = NOTIFICATION_TYPE.TOURNAMENT_REMINDER_TO_SCHOOL;
        } else if (/tournament reminder to union/i.test(errorText)) {
          backendValidationSection = SETTING_DESTINATIONS_TYPE.UNION_NOTIFICATIONS;
          backendHighlightedReminderType = NOTIFICATION_TYPE.TOURNAMENT_REMINDER_TO_UNION;
        }

        this.setState(prevState => ({
          isLoading: false,
          isStatusModalOpen: true,
          statusModalStatus: 'error',
          statusModalTitle: 'Unable to save notification settings',
          statusModalMessage: errorText,
          statusModalLines: [],
          highlightedReminderType: backendHighlightedReminderType,
          collapsedSections: backendValidationSection
            ? {
                ...prevState.collapsedSections,
                [backendValidationSection]: false
              }
            : prevState.collapsedSections
        }));
      });
  };

  onAllNotificationsChange = (setFieldValue: (field: string, value: any) => void, values: FormValues): void => {
    const { isAllNotifications } = values;

    const changedAllSettings = values.allSettings.map(setting => ({
      ...setting,
      status: this.getNotificationStatus(!isAllNotifications)
    }));

    setFieldValue('isAllNotifications', !isAllNotifications);
    setFieldValue('isSchoolNotifications', !isAllNotifications);
    setFieldValue('isUnionNotifications', !isAllNotifications);
    setFieldValue('isHighLevelParticipationNotifications', !isAllNotifications);
    setFieldValue('allSettings', changedAllSettings);
  };

  onGroupSettingsChange = (
    values: FormValues,
    setFieldValue: (field: string, value: any) => void,
    checkType: 'isSchoolNotifications' | 'isUnionNotifications' | 'isHighLevelParticipationNotifications',
    groupSetting: { type: string; name: string }
  ): void => {
    const copyAllSettings = [...values.allSettings];
    const settings = SETTINGS.filter(setting => setting.relation === groupSetting.type);

    const checkedSettings = copyAllSettings.filter(adjustment =>
      settings.some(setting => setting.type === adjustment.type)
    );

    const checkedCopySettings = copyAllSettings.map(copySetting => {
      const isExistSetting = checkedSettings.some(checkedSetting => checkedSetting.type === copySetting.type);

      if (isExistSetting) {
        return {
          ...copySetting,
          status: this.getNotificationStatus(!values[checkType])
        };
      }

      return copySetting;
    });

    const isAutoAllSettings = this.getAllNotificationsStatus(checkedCopySettings);
    setFieldValue('isAllNotifications', isAutoAllSettings);
    setFieldValue('allSettings', checkedCopySettings);
    setFieldValue(checkType, !values[checkType]);
  };

  renderGroupSettingsRow = (
    setting: { type: string; name: string; description?: string },
    values: FormValues,
    setFieldValue: (field: string, value: any) => void
  ): React.ReactNode => {
    const checkType = this.getCheckType(setting.type);
    const isChecked = values[checkType];
    const isCollapsed = !!this.state.collapsedSections[setting.type];
    const groupDefsCount = SETTINGS.filter(def => def.relation === setting.type).length;
    const visualState = this.getGroupVisualState(values.allSettings, setting.type);

    return (
      <div className="nsSection">
        <div className="nsSectionHeader">
          <div
            className="nsSectionTitleWrap"
            onClick={() => this.toggleSectionCollapsed(setting.type)}
            style={{ cursor: 'pointer' }}
          >
            <div className="nsSectionTitleRow">
              <div className="nsSectionTitle">
                {isCollapsed ? '▸' : '▾'} {setting.name}
              </div>

              <div className="nsRecipientBadge">
                {groupDefsCount} notification{groupDefsCount === 1 ? '' : 's'}
              </div>
            </div>
            {setting.description && <p className="nsSectionDescription">{setting.description}</p>}
          </div>

          <div className="nsToggleCell">
            <div className={`nsSectionToggleWrap nsSectionToggleWrap--${visualState}`}>
              <span className="nsToggleLabel">Off</span>
              <Switch
                key={checkType}
                value={isChecked}
                onChange={() => this.onGroupSettingsChange(values, setFieldValue, checkType, setting)}
                text={''}
                name={checkType}
                disabled={!this.state.isEditMode}
              />
              <span className="nsToggleLabel">On</span>
            </div>
          </div>
        </div>

        {!isCollapsed ? <div className="nsRows">{this.renderSettingsRow(setting, values, setFieldValue)}</div> : null}
      </div>
    );
  };

  renderSettingsRow = (
    designationSetting: { type: string; name: string },
    values: FormValues,
    setFieldValue: (field: string, value: any) => void
  ): React.ReactNode => {
    const groupDefs = SETTINGS.filter(def => def.relation === designationSetting.type);

    return groupDefs.map(def => {
      const current = values.allSettings.find(s => s.type === def.type) || {
        type: def.type,
        status: NOTIFICATION_STATUS_TYPE.DISABLED as NotificationStatus
      };

      const isTournamentReminderToSchool = def.type === NOTIFICATION_TYPE.TOURNAMENT_REMINDER_TO_SCHOOL;
      const isTournamentReminderToUnion = def.type === NOTIFICATION_TYPE.TOURNAMENT_REMINDER_TO_UNION;

      const recipientLabel = this.getRecipientLabel(def.hintText);
      const shortHintText = this.getShortHintText(def.hintText);
      const isHighlightedReminder = this.state.highlightedReminderType === def.type;

      return (
        <div
          key={`setting_type_${def.type}`}
          className={`nsRowShell ${isHighlightedReminder ? 'nsRowShell--error' : ''}`}
        >
          <div className="nsRow">
            <div className="nsRowMain">
              <div className="nsRowTitleLine">
                <div className="nsRowTitle">{def.name}</div>
                {recipientLabel ? <div className="nsRecipientBadge">{recipientLabel}</div> : null}
              </div>

              {shortHintText ? <div className="nsRowHint">{shortHintText}</div> : null}
            </div>

            <div className="nsToggleCell">
              <div className="nsToggleWrap">
                <span className="nsToggleLabel">Off</span>
                <Switch
                  key={def.type}
                  value={this.getIsStatusAuto(current.status)}
                  onChange={() => {
                    const allSettings = [...values.allSettings];
                    const currentSettingIndex = allSettings.findIndex(setting => setting.type === def.type);

                    if (currentSettingIndex !== -1) {
                      const updatedSetting = {
                        ...allSettings[currentSettingIndex],
                        status: this.getReverseStatus(allSettings[currentSettingIndex].status)
                      };

                      allSettings.splice(currentSettingIndex, 1, updatedSetting);
                    } else {
                      allSettings.push({
                        type: def.type,
                        status: NOTIFICATION_STATUS_TYPE.AUTO as NotificationStatus
                      });
                    }

                    setFieldValue('allSettings', allSettings);

                    const isAutoAllSettings = this.getAllNotificationsStatus(allSettings);
                    setFieldValue('isAllNotifications', isAutoAllSettings);

                    const isCheckedGroupSettings = this.getGroupSettingStatus(allSettings, designationSetting.type);
                    const checkType = this.getCheckType(designationSetting.type);
                    setFieldValue(checkType, isCheckedGroupSettings);
                  }}
                  text={''}
                  name={def.type}
                  disabled={!this.state.isEditMode}
                />
                <span className="nsToggleLabel">On</span>
              </div>
            </div>
          </div>

          {isTournamentReminderToSchool ? (
            <div className="mb-2">
              {this.renderReminderScheduleInline(
                values,
                setFieldValue,
                'tournamentReminderToSchoolOffsetsMinutes',
                'tournamentReminderToSchoolCustom',
                'Reminder schedule'
              )}
            </div>
          ) : null}

          {isTournamentReminderToUnion ? (
            <div className="mb-2">
              {this.renderReminderScheduleInline(
                values,
                setFieldValue,
                'tournamentReminderToUnionOffsetsMinutes',
                'tournamentReminderToUnionCustom',
                'Reminder schedule'
              )}
            </div>
          ) : null}
        </div>
      );
    });
  };

  renderSettingsList = (values: FormValues, setFieldValue: (field: string, value: any) => void): React.ReactNode => {
    return SETTINGS_DESIGNATIONS.map((setting, index) => {
      const isShowLastEmptyRow = SETTINGS_DESIGNATIONS.length - 1 !== index;

      return (
        <div key={setting.type}>
          {index === 0 && <h2 className="nsContextTitle">Tournaments organised by your union</h2>}
          {this.renderGroupSettingsRow(setting, values, setFieldValue)}
          {isShowLastEmptyRow && <div className="eNotificationSettingEmptyRow" />}
        </div>
      );
    });
  };

  render() {
    const { notificationSettings, isLoading, isEditMode, isHelpOpen } = this.state;

    if (isLoading) {
      return <Loader />;
    }

    const buttonText = isEditMode ? 'Save' : 'Edit';
    const areAllSectionsCollapsed = SETTINGS_DESIGNATIONS.every(
      setting => !!this.state.collapsedSections[setting.type]
    );

    const notificationSettingsForm: FormValues = {
      allSettings: notificationSettings,
      notificationSchedule: {
        tournamentReminderToSchoolOffsetsMinutes: this.state.notificationSchedule
          .tournamentReminderToSchoolOffsetsMinutes,
        tournamentReminderToUnionOffsetsMinutes: this.state.notificationSchedule.tournamentReminderToUnionOffsetsMinutes
      },
      isSchoolNotifications: this.getGroupSettingStatus(
        notificationSettings,
        SETTING_DESTINATIONS_TYPE.SCHOOL_NOTIFICATIONS
      ),
      isUnionNotifications: this.getGroupSettingStatus(
        notificationSettings,
        SETTING_DESTINATIONS_TYPE.UNION_NOTIFICATIONS
      ),
      isHighLevelParticipationNotifications: this.getGroupSettingStatus(
        notificationSettings,
        SETTING_DESTINATIONS_TYPE.HIGH_LEVEL_PARTICIPATION
      ),
      isAllNotifications: this.getAllNotificationsStatus(notificationSettings),

      tournamentReminderToSchoolCustomDays: '',
      tournamentReminderToSchoolCustomHours: '',
      tournamentReminderToSchoolCustomMinutes: '',

      tournamentReminderToUnionCustomDays: '',
      tournamentReminderToUnionCustomHours: '',
      tournamentReminderToUnionCustomMinutes: ''
    };

    return (
      <Formik initialValues={notificationSettingsForm} onSubmit={this.onFormSubmit}>
        {({ setFieldValue, values }) => {
          this.formValuesRef = values;
          return (
            <>
              <HelpModal
                isOpen={isHelpOpen}
                title="About tournament notifications"
                badgeText="Tournament notifications"
                subtitle="Use this page to control which tournament notifications are sent to school admins and union admins, and to choose when tournament reminders should be sent before the start time."
                onClose={() => this.setState({ isHelpOpen: false })}
              >
                <div className="row">
                  <div className="col-md-6 mb-3">
                    <div className="border rounded p-3 h-100">
                      <div className="font-weight-bold mb-2">What this page does</div>
                      <div className="text-muted">
                        This page controls tournament-related system notifications for your union. You can decide which
                        messages are sent automatically, and for tournament reminders you can also choose how far in
                        advance they should be sent.
                      </div>
                    </div>
                  </div>

                  <div className="col-md-6 mb-3">
                    <div className="border rounded p-3 h-100">
                      <div className="font-weight-bold mb-2">Organising and participating</div>
                      <div className="mb-2">
                        <span className="badge badge-light border mr-2">School admins</span>
                        Notifications to school staff for tournaments organised by your union.
                      </div>
                      <div>
                        <span className="badge badge-light border mr-2">Union admins</span>
                        Notifications to your union staff for tournaments you organise.
                      </div>
                    </div>
                  </div>

                  <div className="col-md-6 mb-3">
                    <div className="border rounded p-3 h-100">
                      <div className="font-weight-bold mb-2">Notifications sent to school admins</div>
                      <ul className="mb-0 pl-3 text-muted">
                        <li>
                          <b>Tournament invite has been created</b>
                          <br />
                          Sent when a school is invited to a tournament, including when a school books onto a tournament
                          or waiting list.
                        </li>
                        <li className="mt-2">
                          <b>School has been added as a tournament participant</b>
                          <br />
                          Sent when the school becomes a confirmed participant.
                        </li>
                        <li className="mt-2">
                          <b>School has been added to a tournament waiting list</b>
                          <br />
                          Sent when the school is placed on the waiting list.
                        </li>
                        <li className="mt-2">
                          <b>School has been removed from a tournament waiting list</b>
                          <br />
                          Sent when your school leaves or is removed from the tournament waiting list.
                        </li>
                        <li className="mt-2">
                          <b>School has been removed from a tournament</b>
                          <br />
                          Sent when the school is removed from the tournament.
                        </li>
                      </ul>
                    </div>
                  </div>

                  <div className="col-md-6 mb-3">
                    <div className="border rounded p-3 h-100">
                      <div className="font-weight-bold mb-2">More notifications sent to school admins</div>
                      <ul className="mb-0 pl-3 text-muted">
                        <li>
                          <b>Custom notification</b>
                          <br />
                          Sent when a union admin sends a manual tournament message to selected schools.
                        </li>
                        <li className="mt-2">
                          <b>Tournament details have been updated</b>
                          <br />
                          Sent when important tournament details change, such as date, time or venue.
                        </li>
                        <li className="mt-2">
                          <b>Confirmation request has been received</b>
                          <br />
                          Sent when the union asks schools to confirm their participation.
                        </li>
                        <li className="mt-2">
                          <b>Participation confirmation has been submitted</b>
                          <br />
                          Sent when your school confirms participation in response to a tournament confirmation request.
                        </li>
                        <li className="mt-2">
                          <b>Non-participation confirmation has been submitted</b>
                          <br />
                          Sent when your school confirms that it is not participating in response to a tournament
                          confirmation request.
                        </li>
                        <li className="mt-2">
                          <b>Tournament reminder</b>
                          <br />
                          Sent before the tournament start time, using the reminder schedule you choose below.
                        </li>
                      </ul>
                    </div>
                  </div>

                  <div className="col-md-6 mb-3">
                    <div className="border rounded p-3 h-100">
                      <div className="font-weight-bold mb-2">Notifications sent to union admins</div>
                      <ul className="mb-0 pl-3 text-muted">
                        <li>
                          <b>School has confirmed participation</b>
                          <br />
                          Sent when a school responds to a tournament confirmation request and confirms participation.
                        </li>
                        <li className="mt-2">
                          <b>School has declined participation</b>
                          <br />
                          Sent when a school responds to a tournament confirmation request and confirms that it is not
                          participating.
                        </li>
                        <li className="mt-2">
                          <b>School has been added to the tournament participant list</b>
                          <br />
                          Sent when a school becomes a confirmed participant.
                        </li>
                        <li className="mt-2">
                          <b>School has been added to a tournament waiting list</b>
                          <br />
                          Sent when a school is placed on the waiting list.
                        </li>
                        <li className="mt-2">
                          <b>School has been removed from a tournament waiting list</b>
                          <br />
                          Sent when a school leaves or is removed from the tournament waiting list.
                        </li>
                        <li className="mt-2">
                          <b>School has been removed from a tournament</b>
                          <br />
                          Sent when a school is removed from the tournament.
                        </li>
                        <li className="mt-2">
                          <b>New record has been added</b>
                          <br />
                          Sent when a new tournament record is added.
                        </li>
                        <li className="mt-2">
                          <b>Tournament reminder</b>
                          <br />
                          Sent before the tournament start time, using the reminder schedule you choose below.
                        </li>
                      </ul>
                    </div>
                  </div>

                  <div className="col-md-6 mb-3">
                    <div className="border rounded p-3 h-100">
                      <div className="font-weight-bold mb-2">High-level tournaments · Union participation</div>
                      <p className="text-muted">These settings control availability requests, deadline updates, reminders, overrides and cancellations sent to your athletes’ schools, and school responses sent to your participating union.</p>
                      <p className="text-muted mb-0">To choose which administrators of your union receive school responses, open the tournament and use <b>Notification recipients</b>. Each tournament has its own list and switch.</p>
                    </div>
                  </div>

                  <div className="col-md-6 mb-3">
                    <div className="border rounded p-3 h-100">
                      <div className="font-weight-bold mb-2">How reminder timing works</div>
                      <div className="text-muted mb-2">
                        Tournament reminders can be configured separately for school admins and union admins.
                      </div>
                      <ul className="mb-0 pl-3 text-muted">
                        <li>You can select one interval or several intervals.</li>
                        <li>You can use preset options such as 7 days, 1 day or 1 hour.</li>
                        <li>You can also add your own custom combination of days, hours and minutes.</li>
                        <li>Each selected interval creates a separate reminder before the tournament start time.</li>
                      </ul>
                    </div>
                  </div>

                  <div className="col-md-6 mb-3">
                    <div className="border rounded p-3 h-100">
                      <div className="font-weight-bold mb-2">Important reminder rule</div>
                      <div className="text-muted">
                        A tournament reminder will only be sent when <b>both</b> conditions are met:
                      </div>
                      <ul className="mb-0 pl-3 text-muted mt-2">
                        <li>the relevant reminder notification is switched on</li>
                        <li>at least one reminder interval has been selected</li>
                      </ul>
                    </div>
                  </div>

                  <div className="col-md-6 mb-3">
                    <div className="border rounded p-3 h-100">
                      <div className="font-weight-bold mb-2">Using the switches</div>
                      <div className="text-muted">
                        You can switch notifications on or off individually, by recipient group, or all at once. Group
                        switches control only their own section. High-level tournament participation has its own group.
                      </div>
                    </div>
                  </div>

                  <div className="col-md-6 mb-3">
                    <div className="border rounded p-3 h-100">
                      <div className="font-weight-bold mb-2">Good to know</div>
                      <div className="text-muted">
                        These settings affect future tournament notifications. If tournament details change or schools
                        are added to or removed from a tournament, the reminder flow will follow the latest tournament
                        information and latest participant list.
                      </div>
                    </div>
                  </div>
                </div>

                <div className="border rounded p-3 mt-2" style={{ background: '#fffdf3' }}>
                  <div className="font-weight-bold mb-2">Tip</div>
                  <div className="text-muted mb-0">
                    A practical setup is to keep operational notifications enabled, then use reminder notifications with
                    2 or 3 intervals such as 7 days, 1 day and 1 hour so both schools and union staff get a clear run-up
                    to the event.
                  </div>
                </div>
              </HelpModal>
              <StatusModal
                isOpen={this.state.isStatusModalOpen}
                compact
                status={this.state.statusModalStatus}
                title={this.state.statusModalTitle}
                message={this.state.statusModalMessage}
                lines={this.state.statusModalLines}
                onClose={() =>
                  this.setState({
                    isStatusModalOpen: false,
                    statusModalTitle: '',
                    statusModalMessage: '',
                    statusModalLines: [],
                    statusModalStatus: 'success',
                    highlightedReminderType: ''
                  })
                }
              />
              <UnsavedChangesModal
                isOpen={this.state.isLeaveWarningOpen}
                message="Your notification settings have changed. Discarding will restore the last saved settings."
                onKeepEditing={this.onStayOnPage}
                onDiscard={this.onDiscardChanges}
              />

              <Form>
                <div className="nsPage">
                  <div className="container-fluid">
                    <div className="row">
                      <div className="col-md-12 eNotificationSettings">
                        <div className="nsTopBar nsPageHeader">
                          <div className="nsTopBarMain">
                            <div className="nsHeadingRow">
                              <div>
                                <h1 className="nsTitle">Notification settings</h1>
                              </div>
                            </div>

                            <div className="nsSubtitle">
                              Manage which tournament notifications are sent. Reminder schedules for tournament
                              reminders are configured inline with their related notification types.
                            </div>
                          </div>

                          <div className="nsTopBarActions">
                            <button
                              type="button"
                              className="nsButton nsButton--secondary"
                              onClick={areAllSectionsCollapsed ? this.expandAllSections : this.collapseAllSections}
                              aria-expanded={!areAllSectionsCollapsed}
                            >
                              {areAllSectionsCollapsed ? 'Expand all' : 'Collapse all'}
                            </button>

                            <button
                              type="button"
                              className="nsButton nsButton--secondary"
                              disabled={!isEditMode}
                              onClick={this.onCloseClick}
                            >
                              Cancel
                            </button>

                            <button type="submit" className="nsButton nsButton--primary">
                              {buttonText}
                            </button>
                          </div>
                        </div>

                        <div className="nsCard">
                          <div>
                            <div className="nsTableHead">
                              <div className="nsTableHeadTitle">Notification</div>
                              <div className="nsTableHeadTitle">Status</div>
                            </div>

                            <div className="nsAllRow">
                              <div className="nsAllRowInner">
                                <div>
                                  <div className="nsAllTitle">All notifications</div>
                                  <div className="nsAllHint">Turn all available notification types on or off.</div>
                                </div>

                                <div className="nsToggleCell">
                                  <div className="nsToggleWrap">
                                    <span className="nsToggleLabel">Off</span>
                                    <Switch
                                      key="isAllNotifications"
                                      value={values.isAllNotifications}
                                      onChange={() => this.onAllNotificationsChange(setFieldValue, values)}
                                      text={''}
                                      name="isAllNotifications"
                                      disabled={!isEditMode}
                                    />
                                    <span className="nsToggleLabel">On</span>
                                  </div>
                                </div>
                              </div>
                            </div>

                            {this.renderSettingsList(values, setFieldValue)}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </Form>
            </>
          );
        }}
      </Formik>
    );
  }
}
